import { useDeleteLifeAreaSection } from 'features/life-areas';
import { useDeleteTasks } from 'features/tasks';
import React, { useCallback, useMemo } from 'react';
import { DeleteLifeAreaSectionDialog } from 'shared/components/dialogs/delete-life-area-section-dialog';
import { getTasksByIds } from 'shared/services/backend-api';
import { ID } from 'shared/types/id';
import { LifeArea } from 'shared/types/life-area';

import {
  DeleteLifeAreaSectionContext,
  DeleteLifeAreaSectionContextProps,
} from './delete-life-area-section-context';

export type DeleteLifeAreaSectionProviderProps = {
  children: React.ReactNode;
};

export const DeleteLifeAreaSectionProvider: React.FC<
  DeleteLifeAreaSectionProviderProps
> = ({ children }) => {
  const [lifeArea, setLifeArea] = React.useState<LifeArea>();
  const [sectionId, setSectionId] = React.useState<ID>();
  const deleteLifeAreaSection = useDeleteLifeAreaSection();
  const deleteTasks = useDeleteTasks();

  const section = useMemo(
    () => lifeArea?.sections?.find(({ id }) => id === sectionId),
    [lifeArea, sectionId],
  );

  const openDialog = useCallback((lifeArea: LifeArea, sectionId: ID) => {
    setLifeArea(lifeArea);
    setSectionId(sectionId);
  }, []);

  const closeDialog = useCallback(() => {
    setLifeArea(undefined);
    setSectionId(undefined);
  }, []);

  const submit = useCallback(
    async (withTasks: boolean) => {
      try {
        if (!lifeArea || !section) {
          return;
        }

        const tasksToDelete =
          withTasks && section.tasks?.length
            ? await getTasksByIds(section.tasks)
            : [];
        if (tasksToDelete.length > 0) {
          await deleteTasks(tasksToDelete.map(({ id }) => id));
        }

        deleteLifeAreaSection(lifeArea, section.id, tasksToDelete);
      } finally {
        closeDialog();
      }
    },
    [closeDialog, deleteLifeAreaSection, deleteTasks, lifeArea, section],
  );

  const value = useMemo<DeleteLifeAreaSectionContextProps>(
    () => ({ onDeleteLifeAreaSection: openDialog }),
    [openDialog],
  );

  return (
    <DeleteLifeAreaSectionContext.Provider value={value}>
      {children}
      <DeleteLifeAreaSectionDialog
        open={!!section}
        name={section?.name}
        onClose={closeDialog}
        onSubmit={submit}
      />
    </DeleteLifeAreaSectionContext.Provider>
  );
};
